import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable } from 'primereact/datatable'
import { selectAllAssets } from 'providers/AssetLoader/store/selectors'
import { Toast } from 'primereact/toast'
import * as Columns from './Columns'
import { useGridDataSync } from './hooks/useGridDataSync'
import { useRowEditing } from './hooks/useRowEditing'
import { createColumn } from './ColumnFactory'
import {
    PAGINATOR_POSITION,
    PAGINATOR_TEMPLATE,
    CURRENT_PAGE_REPORT_TEMPLATE,
    DEFAULT_ROWS_PER_PAGE_STATIC,
    ROWS_PER_PAGE_OPTIONS_STATIC
} from './config/gridConstants'
import { assembleColumns } from './utils/assembleColumns'
import { matchConstraints } from 'modules/filterUtils'

// ReSharper disable once InconsistentNaming
const StaticGrid = forwardRef((props, ref) => {
    const dataTableRef = useRef(null)
    const toastRef = useRef(null)
    const {
        dataKey,
        episodeId,
        totalCount,
        emptyMessage,
        columnDefinitions,
        showPaginator,
        header,
        selectionMode,
        selectionStyle,
        scrollHeight,
        rowPerPage,
        allowRowEdits,
        originalData,
        startEditing,
        stopEditing,
        setFilterAction,
        setSortAction,
        generateNewRowKey,
        onRowCancelCustom,
        // Selection props
        selection,
        onSelectionChange,
        onRowClick,
        rowClassName,
        onKeyDown
    } = props

    // Ref to store editor callbacks for cross-field updates (e.g., percentage <-> minutes)
    const editorCallbacksRef = useRef({})

    const [multiSortMeta, setMultiSortMeta] = useState([])
    const [filters, setFilters] = useState(null)
    const [editingRowKey, setEditingRowKey] = useState(null)
    const [expandedRows, setExpandedRows] = useState(null)
    const [editingRows, setEditingRows] = useState({})

    // Track ALL rows that are being edited (supports multiple rows in edit mode)
    const editingRowKeysSetRef = useRef(new Set())
    const editingRowKeyRef = useRef(null)

    // Update refs whenever editingRowKey changes
    useEffect(() => {
        editingRowKeyRef.current = editingRowKey

        if (editingRowKey !== null) {
            // Add row to Set when it enters edit mode
            editingRowKeysSetRef.current.add(editingRowKey)
        }
        // DON'T remove from Set here - removal happens via removeFromEditingSet callback
    }, [editingRowKey])

    // Callback to remove a specific row from the editing Set
    // Called by useRowEditing when a row is saved or canceled
    const removeFromEditingSet = useCallback((rowKey) => {
        editingRowKeysSetRef.current.delete(rowKey)
    }, [])

    const handleRowToggle = (e) => {
        setExpandedRows(e.data)
    }

    const { localData, setLocalData, selectedChannels, setSelectedChannels, sanitizedFilters } = useGridDataSync({
        data: props.data,
        episodeId,
        draftAvailable: props.draftAvailable,
        id: props.id,
        dataKey,
        filters,
        editingRowKey
    })

    const dispatch = useDispatch()
    const {
            channels
        } = useSelector(selectAllAssets)

    const {
        validationErrors,
        openNewRowInEditMode,
        onRowEditInit,
        onRowEditSave,
        rowEditValidator,
        onRowEditCancel,
        updateRow,
        updateRowField,
        updateRowFields
    } = useRowEditing({
        dataKey,
        columnDefinitions,
        localData,
        setLocalData,
        originalData,
        data: props.data,
        selectedChannels,
        setSelectedChannels,
        validateData: props.validateData,
        onSave: props.onSave,
        startEditing,
        stopEditing,
        generateNewRowKey,
        onRowCancelCustom,
        onRowEditInitCustom: props.onRowEditInit,
        toastRef: props.toastRef,
        editingRowKey,
        setEditingRowKey,
        removeFromEditingSet
    })


    //TODO: Andy L says useImperativeHandle is best practise
    useImperativeHandle(ref, () => ({
        getData: () => {
            return localData
        },
        addNewRow: (initialData) => {
            openNewRowInEditMode(initialData)
        },
        updateRow,
        clearChannelSelection: (keyValue) => {
            // Remove channel selection for a specific row key
            // Used when deleting rows to prevent stale channel values
            if (selectedChannels.current && keyValue in selectedChannels.current) {
                const { [keyValue]: _, ...remainingChannels } = selectedChannels.current
                setSelectedChannels(remainingChannels)
            }
        },
        getFilteredSortedData: () => {
            let data = localData || props.data

            // Apply filters
            if (filters) {
                data = data.filter(item => {
                    return Object.keys(filters).every(field => {
                        const filterConstraint = filters[field]
                        if (!filterConstraint) return true

                        // Skip empty filter constraints (PrimeReact includes all columns in filters object)
                        if (filterConstraint.constraints && Array.isArray(filterConstraint.constraints)) {
                            const hasActiveConstraint = filterConstraint.constraints.some(c => c.value !== null && c.value !== undefined && c.value !== '')
                            if (!hasActiveConstraint) return true
                        }

                        return matchConstraints(item[field], filterConstraint)
                    })
                })
            }

            // Apply sorts
            if (multiSortMeta && multiSortMeta.length > 0) {
                data = [...data].sort((a, b) => {
                    for (let i = 0; i < multiSortMeta.length; i++) {
                        const { field, order } = multiSortMeta[i]
                        let result = 0

                        const aValue = a[field]
                        const bValue = b[field]

                        // Handle null/undefined
                        if (aValue == null && bValue == null) {
                            result = 0
                        } else if (aValue == null) {
                            result = 1
                        } else if (bValue == null) {
                            result = -1
                        } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                            result = aValue.localeCompare(bValue)
                        } else {
                            result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
                        }

                        if (result !== 0) {
                            return order * result
                        }
                    }
                    return 0
                })
            }

            return data
        }
    }))
    const isFieldChanged = (rowData, field) => {
        if (!originalData) {
            return false
        }
        const originalRowData = Object.values(originalData)
            .find(row => row[dataKey] === rowData[dataKey])

        if (!originalRowData) {
            return true
        }

        const originalValue = originalRowData[field]
        const currentValue = rowData[field]

        return (originalValue instanceof Date && currentValue instanceof Date)
                   ? originalValue.getTime() !== currentValue.getTime()
                   : originalValue !== currentValue
    }

    const isDateFieldChanged = (rowData, field) => {
        if (!originalData) return false;
        const originalRowData = originalData.find(row => row[dataKey] === rowData[dataKey]);
        if (!originalRowData) return true;

        const originalValue = originalRowData[field];
        const currentValue = rowData[field];

        return originalValue.getFullYear() !== currentValue.getFullYear() ||
               originalValue.getMonth() !== currentValue.getMonth() ||
               originalValue.getDate() !== currentValue.getDate();
    }

    const isTimeFieldChanged = (rowData, field) => {
        if (!originalData) return false;
        const originalRowData = originalData.find(row => row[dataKey] === rowData[dataKey]);
        if (!originalRowData) return true;

        const originalValue = originalRowData[field];
        const currentValue = rowData[field];

        return originalValue.getHours() !== currentValue.getHours() || originalValue.getMinutes() !== currentValue.getMinutes();
    }


    const onFilter = (e) => {
        setFilters(e.filters)
        if (setFilterAction) {
            dispatch(setFilterAction(e.filters))
        }
    }

    const onSort = (e) => {
        setMultiSortMeta(e.multiSortMeta)
        if (setSortAction) {
            dispatch(setSortAction(e.multiSortMeta))
        }
    }

    // Create all columns from definitions
    const definedColumns = columnDefinitions.map((col) =>
        createColumn({
            columnType: col.columnType,
            title: col.title,
            mapsToField: col.mapsToField,
            filter: col.filter,
            expand: col.expand,
            template: col.template,
            sortable: col.sortable,
            filterMatchMode: col.filterMatchMode,
            filterElement: col.filterElement,
            editable: col.editable,
            isFieldChanged,
            isDateFieldChanged,
            isFieldChangedOverride: col.isFieldChangedOverride,
            isTimeFieldChanged,
            validationErrors,
            dataKey,
            editingRowKey,
            selectedChannels,
            setSelectedChannels,
            channels,
            contributionTypes: props.contributionTypes,
            contributorNames: props.contributorNames,
            originalData,
            min: col.min,
            max: col.max,
            episodeDuration: props.episodeDuration,
            updateRowField,
            updateRowFields,
            editorCallbacksRef,
            toastRef: props.toastRef,
            className: col.className
        })
    )

    // Assemble final column array with special columns
    // Pass both editingRowKey (value) and editingRowKeysSetRef (Set of all editing rows) to ensure:
    // 1. Columns are recreated when editing state changes (using value as dependency)
    // 2. Column templates can check if ANY row is in the editing Set (using Set ref)
    const columns = assembleColumns({
        definedColumns,
        columnDefinitions,
        selectionMode,
        allowRowEdits,
        Columns,
        dataKey,
        editingRowKey,
        editingRowKeyRef,
        editingRowKeysSetRef,
        expandedRows,
        onRowToggle: handleRowToggle
    })

    //TODO : Andy L says the scroll height being set this way is not ideal. It should be set in the parent component.
    return (
            <div id={props.id + '_container'}>
                <Toast ref={toastRef}/>
                <DataTable
                    ref={dataTableRef}
                    id={props.id + '_container_dataTable'}
                    value={localData}
                    size="small"
                    emptyMessage={emptyMessage}
                    selectionMode={selectionStyle === 'checkbox' ? 'checkbox' : 'single'}
                    scrollable
                    scrollHeight={scrollHeight ? scrollHeight : (showPaginator ? '250px' : '290px')}
                    showGridlines
                    stripedRows
                    removableSort
                    filters={sanitizedFilters}
                    paginator={showPaginator}
                    paginatorPosition={PAGINATOR_POSITION}
                    paginatorTemplate={PAGINATOR_TEMPLATE}
                    currentPageReportTemplate={CURRENT_PAGE_REPORT_TEMPLATE}
                    rows={rowPerPage || DEFAULT_ROWS_PER_PAGE_STATIC}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS_STATIC}
                    className="datatable-responsive"
                    dataKey={dataKey}
                    multiSortMeta={multiSortMeta}
                    sortMode="multiple"
                    onSort={onSort}
                    totalRecords={totalCount}
                    {...(props.rowExpansionTemplate && {
                        expandedRows: expandedRows,
                        rowExpansionTemplate: props.rowExpansionTemplate,
                        onRowToggle: handleRowToggle
                    })}
                    header={header}
                    onFilter={onFilter}
                    editMode="row"
                    editingRows={editingRows}
                    onRowEditChange={(e) => setEditingRows(e.data)}
                    rowEditValidator={rowEditValidator}
                    onRowEditCancel={onRowEditCancel}
                    onRowEditInit={onRowEditInit}
                    onRowEditSave={onRowEditSave}
                    selection={selection}
                    onSelectionChange={onSelectionChange}
                    onRowClick={onRowClick}
                    rowClassName={rowClassName}
                    onKeyDown={onKeyDown}>

                    {columns}

                </DataTable>
            </div>
        )
})

export default StaticGrid
